import { useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from '@finn-auto/design-system/atoms/button';
import { confirmSignup, JoinError, type JoinLanguage } from '../../services/talentJoinApi';
import { JOIN_COPY, initialLanguage } from './joinCopy';
import { JoinShell } from './JoinShell';
import { useJoinConfig } from './useJoinConfig';

/**
 * PUBLIC: /join/confirm/:token — where the confirmation email's button lands.
 *
 * The consent is given by pressing the button HERE, not by opening the link: mail scanners open
 * every link in an email, and a consent recorded by a scanner proves nothing. So loading this page
 * changes nothing; the press does.
 */
export default function JoinConfirmView() {
  const { token = '' } = useParams<{ token: string }>();
  const { search } = useLocation();
  const [language, setLanguage] = useState<JoinLanguage>(() => initialLanguage(search, navigator.language));
  const config = useJoinConfig();
  const copy = JOIN_COPY[language];
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'invalid' | 'error'>('idle');
  const [manageToken, setManageToken] = useState('');
  const pressed = useRef(false);

  async function confirm() {
    if (pressed.current) return;
    pressed.current = true;
    setState('busy');
    try {
      const res = await confirmSignup(token);
      setManageToken(res.manageToken);
      setState('done');
    } catch (err) {
      setState(err instanceof JoinError && err.status === 404 ? 'invalid' : 'error');
      pressed.current = false;
    }
  }

  return (
    <JoinShell language={language} onLanguage={setLanguage} live={config.data?.live}>
      {state === 'done' ? (
        <div>
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">{copy.confirmedTitle}</h1>
          <p className="mb-6 text-gray-700">{copy.confirmedBody}</p>
          <Link to={`/join/manage/${encodeURIComponent(manageToken)}?lang=${language}`} className="font-medium text-gray-900 underline">
            {copy.manageLink}
          </Link>
          <p className="mt-2 text-sm text-gray-600">{copy.bookmark}</p>
        </div>
      ) : state === 'invalid' ? (
        <p className="text-gray-700">{copy.invalidLink}</p>
      ) : (
        <div>
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">{copy.confirmTitle}</h1>
          <p className="mb-6 text-gray-700">{copy.confirmBody}</p>
          <Button variant="primary" loading={state === 'busy'} onClick={confirm}>
            {copy.confirmButton}
          </Button>
          {state === 'error' && <p role="alert" className="mt-4 text-sm text-red-700">{copy.errors.failed}</p>}
        </div>
      )}
    </JoinShell>
  );
}
